import { useCallback, useEffect, useState } from "react";
import { API_BASE_URL } from "../config/api";
import "./clientDirectory.css";

const emptyForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  notes: "",
  defaultPreferences: {
    machineType: "",
    machineCategory: "",
    trailerType: "",
    axleConfiguration: "",
    commonOrigin: "",
    commonDestination: ""
  }
};

export default function ClientDirectory({ onUseClient }) {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  const fetchClients = useCallback(async (query = "") => {
    try {
      setLoading(true);
      const url = query
        ? `${API_BASE_URL}/api/clients?q=${encodeURIComponent(query)}`
        : `${API_BASE_URL}/api/clients`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to load clients");
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      setMessageType("error");
      setMessage(err?.message || "Failed to load clients");
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchClients(search), 250);
    return () => clearTimeout(timer);
  }, [search, fetchClients]);

  const loadIntoEditor = (client) => {
    setSelectedId(client._id);
    setForm({
      name: client.name || "",
      company: client.company || "",
      email: client.email || "",
      phone: client.phone || "",
      notes: client.notes || "",
      defaultPreferences: {
        machineType: client.defaultPreferences?.machineType || "",
        machineCategory: client.defaultPreferences?.machineCategory || "",
        trailerType: client.defaultPreferences?.trailerType || "",
        axleConfiguration: client.defaultPreferences?.axleConfiguration || "",
        commonOrigin: client.defaultPreferences?.commonOrigin || "",
        commonDestination: client.defaultPreferences?.commonDestination || ""
      }
    });
  };

  const handleNew = () => {
    setSelectedId("");
    setForm(emptyForm);
    setMessage("");
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setMessageType("error");
      setMessage("Client name is required.");
      return;
    }
    try {
      setSaving(true);
      setMessage("");
      const method = selectedId ? "PUT" : "POST";
      const url = selectedId
        ? `${API_BASE_URL}/api/clients/${selectedId}`
        : `${API_BASE_URL}/api/clients`;
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to save client");
      setMessageType("success");
      setMessage(selectedId ? "Client updated." : "Client saved.");
      loadIntoEditor(data);
      fetchClients(search);
    } catch (err) {
      setMessageType("error");
      setMessage(err?.message || "Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this client permanently?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/clients/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to delete client");
      if (selectedId === id) handleNew();
      fetchClients(search);
      setMessageType("success");
      setMessage("Client deleted.");
    } catch (err) {
      setMessageType("error");
      setMessage(err?.message || "Failed to delete client");
    }
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updatePref = (field, value) => {
    setForm((prev) => ({
      ...prev,
      defaultPreferences: { ...prev.defaultPreferences, [field]: value }
    }));
  };

  return (
    <div className="client-directory">
      <div className="client-directory-header">
        <div>
          <h2>Client Directory</h2>
          <p className="subtitle">Save clients and reuse details for faster job customization.</p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => fetchClients(search)}>
          Refresh
        </button>
      </div>

      <div className="client-search-row">
        <input
          placeholder="Search by name, company, email, phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="button" onClick={handleNew}>New Client</button>
      </div>

      {message && <p className={messageType === "error" ? "error" : "success"}>{message}</p>}
      {loading && <p className="muted">Loading clients...</p>}

      <div className="client-directory-grid">
        <div className="client-list-panel">
          {!loading && clients.length === 0 && <p className="muted">No clients saved yet.</p>}
          {clients.map((client) => (
            <div key={client._id} className={`client-card ${selectedId === client._id ? "active" : ""}`}>
              <strong>{client.name}</strong>
              <p>{client.company || "No company"}</p>
              <p className="muted">{client.email || client.phone || "No contact"}</p>
              <div className="client-card-actions">
                <button type="button" onClick={() => loadIntoEditor(client)}>Edit</button>
                <button type="button" onClick={() => onUseClient?.(client)}>Use for Quote</button>
                <button type="button" className="danger" onClick={() => handleDelete(client._id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>

        <div className="client-editor-panel">
          <h3>{selectedId ? "Edit Client" : "New Client"}</h3>
          <input placeholder="Client Name *" value={form.name} onChange={(e) => updateForm("name", e.target.value)} />
          <input placeholder="Company" value={form.company} onChange={(e) => updateForm("company", e.target.value)} />
          <input placeholder="Email" type="email" value={form.email} onChange={(e) => updateForm("email", e.target.value)} />
          <input placeholder="Phone" value={form.phone} onChange={(e) => updateForm("phone", e.target.value)} />
          <textarea placeholder="Notes (billing, site access, etc.)" value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} />

          <h4>Default Job Preferences</h4>
          <p className="muted">Applied when you select this client in Create Quote.</p>
          <select value={form.defaultPreferences.machineType} onChange={(e) => updatePref("machineType", e.target.value)}>
            <option value="">Default Machine Type</option>
            <option value="Excavator">Excavator</option>
            <option value="Crane">Crane</option>
            <option value="Bulldozer">Bulldozer</option>
            <option value="Transformer">Transformer</option>
            <option value="Other">Other</option>
          </select>
          <select value={form.defaultPreferences.machineCategory} onChange={(e) => updatePref("machineCategory", e.target.value)}>
            <option value="">Default Category</option>
            <option value="Construction">Construction</option>
            <option value="Mining">Mining</option>
            <option value="Energy">Energy</option>
            <option value="Industrial">Industrial</option>
          </select>
          <select value={form.defaultPreferences.trailerType} onChange={(e) => updatePref("trailerType", e.target.value)}>
            <option value="">Default Trailer</option>
            <option value="Lowbed">Lowbed</option>
            <option value="Extendable Lowbed">Extendable Lowbed</option>
            <option value="Multi-Axle Trailer">Multi-Axle Trailer</option>
          </select>
          <select value={form.defaultPreferences.axleConfiguration} onChange={(e) => updatePref("axleConfiguration", e.target.value)}>
            <option value="">Default Axle Config</option>
            <option value="4x2">4x2</option>
            <option value="6x4">6x4</option>
            <option value="8x4">8x4</option>
            <option value="10x6">10x6</option>
          </select>
          <input placeholder="Common Origin" value={form.defaultPreferences.commonOrigin} onChange={(e) => updatePref("commonOrigin", e.target.value)} />
          <input placeholder="Common Destination" value={form.defaultPreferences.commonDestination} onChange={(e) => updatePref("commonDestination", e.target.value)} />

          <button type="button" className="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : selectedId ? "Update Client" : "Save Client"}
          </button>
        </div>
      </div>
    </div>
  );
}
